import { Component, OnInit, inject, HostListener, signal, ViewChild, AfterViewInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { ThemeService } from './core/services/theme.service';
import { AuthService } from './services/auth.service';
import { AppHeaderComponent } from './components/shared/app-header/app-header.component';
import { FooterComponent } from './components/shared/footer/footer';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, AppHeaderComponent, FooterComponent],
  template: `
    <div class="app-container">
      <!-- ✅ Sentry Test Button (Development Only) -->
      <button *ngIf="showSentryTestButton()" class="sentry-test-btn" (click)="throwTestError()">
        Test Sentry Error
      </button>

      <app-header *ngIf="!isMinimalLayoutPage()" (loginRequest)="onLoginRequest()"></app-header>
      <main class="main-content" [class.scrolled]="isScrolled()" [class.home-page]="isHomePage()" [class.no-header]="isMinimalLayoutPage()">
        <router-outlet></router-outlet>
      </main>
      <app-footer *ngIf="!isMinimalLayoutPage()"></app-footer>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-height: 100vh;
      font-family: 'DM Sans', sans-serif;
      margin: 0;
      padding: 0;
    }

    .app-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      margin: 0;
      padding: 0;
    }

    .main-content {
      flex: 1;
      padding-top: 80px;
      transition: padding-top 0.3s ease;
    }

    .main-content.scrolled {
      padding-top: 64px;
    }

    .main-content.no-header {
      padding-top: 0;
    }

    app-header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      transition: all 0.3s ease;
    }

    app-header.shrink {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
      .main-content {
        padding-top: 70px;
      }

      .main-content.scrolled {
        padding-top: 56px;
      }
    }

    .main-content.home-page {
      padding-top: 0;
      margin: 0;
      width: 100%;
      position: relative;
    }

    /* Sentry Test Button Styles */
    .sentry-test-btn {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
      padding: 12px 24px;
      background: var(--md-sys-color-error, #BA1A1A);
      color: var(--md-sys-color-on-error, #FFFFFF);
      border: none;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      transition: all 0.2s ease;
    }

    .sentry-test-btn:hover {
      background: var(--md-sys-color-error-container, #FFDAD6);
      color: var(--md-sys-color-on-error-container, #410002);
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    }

    .sentry-test-btn:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  `]
})
export class App implements OnInit, AfterViewInit {
  title = 'FARTÜRK Registry';
  isScrolled = signal(false);

  private themeService = inject(ThemeService);
  private router = inject(Router);
  private authService = inject(AuthService);

  ngOnInit(): void {
    // Theme service initializes automatically
    // Track route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Force change detection when route changes
    });
  }

  ngAfterViewInit(): void {
    // Component view initialized
  }

  isHomePage(): boolean {
    return this.router.url === '/' || this.router.url === '';
  }

  isMinimalLayoutPage(): boolean {
    // Pages that should have minimal layout (no header/footer)
    const url = this.router.url;

    // Logout homepage (guest gateway) - minimal layout
    if ((url === '/' || url === '') && !this.authService.currentUser()) {
      return true;
    }

    // Other minimal layout pages
    return url.includes('/patients/new') ||
           url.includes('/visits/new') ||
           url.includes('/invitation-accept');
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isScrolled.set(scrollPosition > 50);
  }

  onLoginRequest() {
    // If we're on the home page, broadcast an event for the home component to open modal
    // Otherwise navigate to login page
    if (this.router.url === '/' || this.router.url === '') {
      // Dispatch a custom event that HomeComponent can listen to
      window.dispatchEvent(new CustomEvent('openLoginModal'));
    } else {
      this.router.navigate(['/login']);
    }
  }

  /**
   * ✅ Sentry Test: Show test button only in development mode
   */
  showSentryTestButton(): boolean {
    return !environment.production &&
           !this.router.url.includes('/login') &&
           !this.router.url.includes('/register') &&
           !this.isMinimalLayoutPage();
  }

  /**
   * ✅ Sentry Test: Throw a test error to verify Sentry integration
   */
  throwTestError(): void {
    throw new Error('Sentry Test Error: This is a intentional test error to verify Sentry integration');
  }
}
